// ─── Whose clock is it ───────────────────────────────────────────────────────
//
// One conversion between a wall clock and an instant, for the whole platform.
//
// ── Why this is its own package ─────────────────────────────────────────────
//
// It began inside `@nulife/booking-ui`, because the bug that produced it was a
// screen showing the wrong hour. Recurrence needed the same arithmetic on the
// SERVER — expanding "every Tuesday at 19:00" has to keep saying 19:00 across
// a clock change — and there were only three ways to get it there:
//
//   * have a server module depend on a React package, which would drag React
//     into a build that has no business containing it;
//   * copy it, and own two implementations of the most error-prone arithmetic
//     in the product;
//   * or lift it out, which is this.
//
// `@nulife/booking-ui` still exports the same two functions from the same
// names, so nothing that used it changed.
//
// ── The two layers, and why the lower one exists ────────────────────────────
//
// `wallClockAt` / `instantOf` are the primitive: a wall clock is a BAG OF
// FIELDS, which is what it actually is. Nothing is lost in the representation.
//
// `toZonedWallClock` / `fromZonedWallClock` carry a wall clock inside a `Date`
// instead, because components call `getHours()` and `toLocaleTimeString()` and
// threading a field bag through every one of them would be a far larger
// change. That convenience costs one real defect, documented on
// `toZonedWallClock` below: a `Date` cannot hold a wall clock that does not
// exist in the RUNTIME's zone. The field bag has no such hole, which is why
// the server side uses it directly.
//
// ── The bug that made this file exist ───────────────────────────────────────
//
// Found on 2026-09-10 by driving the assembled app in a real browser. The
// salon opens at 09:00 London. The test machine runs on UTC. The customer was
// offered "08:00" — the correct instant, labelled with the wrong clock — and
// the booking landed in the database at 09:00 London while the screen had
// said eight.
//
// What somebody is told is the time they must physically be somewhere, so it
// has to be the BUSINESS'S clock, always.

/**
 * A wall clock: what a particular zone's clock and calendar read.
 *
 * `month` is 1–12, because every other part of a date is one-based and a lone
 * zero-based field is a bug waiting for a tired afternoon.
 *
 * This is NOT an instant. On its own it does not name a moment in time — two
 * of these an hour apart can read identically on the Sunday the clocks go
 * back, and one of them can name no moment at all on the Sunday they go
 * forward. It becomes a moment only once a zone is applied to it.
 */
export type WallClock = {
  year: number;
  /** 1–12. */
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  ms: number;
};

/**
 * One formatter per zone, built once.
 *
 * Constructing an `Intl.DateTimeFormat` is expensive; formatting with one is
 * not. This runs for every slot and every booking on the screen — a customer
 * page showing ninety days across three services converts thousands of dates
 * — so building a formatter each time is the difference between a page that
 * appears and a page that hangs.
 *
 * Learned the expensive way one layer down: the booking module's availability
 * engine had the same mistake and one calendar request took 1.2 seconds.
 * Hoisting it there made it 87ms.
 *
 * The cache is keyed by zone and never invalidated, which is correct — a
 * zone's formatter does not change, and there are a few hundred zones in
 * existence.
 */
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  const cached = formatters.get(timeZone);
  if (cached) return cached;
  // `en-CA` gives ISO-ordered, unambiguous numerals.
  const made = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    // `hourCycle: "h23"` and NOT `hour12: false`.
    //
    // The two look interchangeable and are not. `hour12: false` leaves the
    // choice between the h23 and h24 cycles to the locale, and h24 renders
    // midnight as "24" — which, read as an hour, is a whole day out for one
    // hour every night. The previous version relied on `en-CA` happening to be
    // h23 and carried a `% 24` guard that could never fire, so no test could
    // reach it: five minutes of defensive code that was permanently unproven.
    //
    // Asking for h23 outright makes the invariant structural instead. ECMA-402
    // also specifies that `hour12` WINS over `hourCycle` when both are given,
    // so passing both would silently undo this.
    //
    // Honest note: swapping this back to `hour12: false` passes every test in
    // the suite, because `en-CA` is h23 on this engine. It stays as
    // `hourCycle` because it states the requirement rather than depending on a
    // locale's default — but no test here can tell the two apart, and claiming
    // otherwise would be exactly the unearned confidence the rest of this file
    // exists to avoid. Asking for h24 IS caught, by seventy assertions.
    hourCycle: "h23",
  });
  formatters.set(timeZone, made);
  return made;
}

/** What `timeZone`'s clock reads at this instant. */
export function wallClockAt(instant: Date, timeZone: string): WallClock {
  const parts = formatterFor(timeZone).formatToParts(instant);
  const get = (type: string) => Number(parts.find(p => p.type === type)!.value);
  return {
    year: get("year"), month: get("month"), day: get("day"),
    hour: get("hour"), minute: get("minute"), second: get("second"),
    // Milliseconds are not among the parts Intl reports, and they do not
    // differ between zones anyway — no zone is offset by a fraction of a
    // second — so they are carried straight through.
    ms: instant.getMilliseconds(),
  };
}

/** The same fields, read as if they were UTC. The comparison currency used
 *  throughout this file, and the carrier a recurrence rule expands in. */
function asIfUtc(w: Omit<WallClock, "ms"> & { ms?: number }): number {
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second, w.ms ?? 0);
}

/** How far ahead of UTC `timeZone` is at this instant, in milliseconds. */
export function offsetMsAt(instant: Date, timeZone: string): number {
  const w = wallClockAt(instant, timeZone);
  // Compare on whole seconds: `wallClockAt` takes its milliseconds from the
  // instant itself, so including them here would cancel to zero and prove
  // nothing.
  return asIfUtc({ ...w, ms: 0 }) - Math.floor(instant.getTime() / 1000) * 1000;
}

/**
 * The instant at which `timeZone`'s clock reads this.
 *
 * Two passes, because the offset depends on the answer. The first guess uses
 * the offset at roughly the right moment; the second corrects it when the
 * guess landed on the other side of a clock change. Without that, every time
 * on the day the clocks go forward is an hour out. A third pass is checked by
 * the sweep to change nothing, so two really is enough.
 *
 * ── `near`, and why an inverse needs a hint at all ──────────────────────────
 *
 * On the morning the clocks go back, an hour of wall clock happens TWICE. A
 * wall clock inside it names two instants an hour apart, and nothing in the
 * value itself says which — so any inverse must guess, and a wrong guess puts
 * an appointment an hour from where the customer believes it is.
 *
 * Callers usually know the answer. A slot came from the server as an instant;
 * a booking being dragged started at one; a recurring entry has its first
 * occurrence. Passing that as `near` resolves the ambiguity by proximity
 * instead of by luck.
 *
 * Better still, do not invert at all when the original instant is to hand —
 * see the Crafting Comb client, which carries it through the booking journey
 * so the customer's chosen time is never re-derived.
 *
 * ── A wall clock that never happens ─────────────────────────────────────────
 *
 * The spring morning skips an hour, and a wall clock inside it names no
 * instant at all. There is no right answer; this settles on the instant just
 * after the change rather than looping or throwing, which is what a booking
 * form and a recurring meeting both want — never a crash, and never a silent
 * hour's drift for the ordinary times either side.
 */
export function instantOf(
  wall: WallClock, timeZone: string, opts: { near?: Date } = {},
): Date {
  const target = asIfUtc(wall);
  let instant = new Date(target - offsetMsAt(new Date(target), timeZone));
  instant = new Date(target - offsetMsAt(instant, timeZone));

  if (!opts.near) return instant;

  // The other candidate, if this wall clock is one of the repeated ones. Both
  // offsets around a backward change are tried, and a candidate counts only if
  // it genuinely reads back as the wall clock asked for — so a hint can never
  // drag an unambiguous time somewhere else.
  const candidates = [instant];
  for (const shiftMs of [-2, -1, 1, 2].map(h => h * 3_600_000)) {
    const other = new Date(instant.getTime() + shiftMs);
    const readsBack = asIfUtc({ ...wallClockAt(other, timeZone), ms: 0 });
    if (readsBack === Math.floor(target / 1000) * 1000) candidates.push(other);
  }

  const near = opts.near.getTime();
  return candidates.reduce((best, c) =>
    Math.abs(c.getTime() - near) < Math.abs(best.getTime() - near) ? c : best);
}

// ─── The same thing, carried inside a Date ───────────────────────────────────
//
// For the components. See the header: this layer is a convenience with one
// documented hole, and anything that can use the field bag above should.

/**
 * An instant, as a Date whose LOCAL fields read as the wall clock in `timeZone`.
 *
 * Give this to the components. `getHours()` on the result is the hour the
 * business's clock shows, so a block lands in the right row and is labelled
 * with the right time regardless of where the viewer is.
 *
 * The result is not the same moment in time. It is a wall clock wearing a
 * Date's clothes. Convert it back with `fromZonedWallClock` before it goes
 * anywhere near the server.
 *
 * ── Built from fields, not by shifting ──────────────────────────────────────
 *
 * The obvious implementation adds the business's offset and subtracts the
 * viewer's. It is wrong when the two zones sit on opposite sides of a clock
 * change — the viewer's offset has to be taken at the moment that will READ
 * as the target wall clock, which is not the moment you have. Caught by the
 * tests only once they started running with the viewer somewhere other than
 * UTC: a New York viewer looking at a New York salon at 03:30 on the spring
 * Sunday was shown 04:30.
 *
 * Handing the fields to the local `Date` constructor sidesteps the question
 * entirely — it resolves the viewer's own DST itself.
 *
 * ── The one case this cannot represent, and it is the clock's fault ─────────
 *
 * A `Date` can only hold wall clocks that EXIST in the viewer's zone. On the
 * morning the viewer's own clocks go forward, an hour of local time does not
 * exist — thirty minutes on Lord Howe Island — and `new Date(…)` silently
 * moves any value inside it to the far side of the gap.
 *
 * So a business whose clock reads 02:22 cannot be displayed exactly to a
 * viewer on Lord Howe on 4 October 2026: they are shown 02:52. Found by
 * sweeping ten business zones against three viewer zones across a year; it
 * affects the viewer's transition window and nothing else, which for a salon
 * open 09:00–17:00 means never.
 *
 * It is not fixable within this representation — there is no Date with those
 * local fields — and it is precisely why `wallClockAt` exists alongside it.
 * Recorded rather than hidden, and bounded by a test that fails if it ever
 * grows beyond those windows.
 */
export function toZonedWallClock(instant: Date, timeZone: string): Date {
  const w = wallClockAt(instant, timeZone);
  return new Date(w.year, w.month - 1, w.day, w.hour, w.minute, w.second, w.ms);
}

/** The inverse: a wall-clock Date back to the instant it means in `timeZone`. */
export function fromZonedWallClock(
  wall: Date, timeZone: string, opts: { near?: Date } = {},
): Date {
  return instantOf({
    year: wall.getFullYear(), month: wall.getMonth() + 1, day: wall.getDate(),
    hour: wall.getHours(), minute: wall.getMinutes(),
    second: wall.getSeconds(), ms: wall.getMilliseconds(),
  }, timeZone, opts);
}
